import React, { memo, useState } from "react";
import ReactDOM from "react-dom";
import { Check, CheckCheck, Download, FileText, Pin, Trash2, Reply, MoreVertical } from "lucide-react";
import MessageActions from "../MessageActions/MessageActions";

const MessageBubble = memo(({
    msg,
    isMe,
    currentUser,
    activeChatName,
    isGroup = false,
    allUsers = [],
    chatTheme = null,
    onReply,
    onPin,
    onUnpin,
    onTask,
    onDelete,
    onViewImage,
    onReminder,
    canPin = true,
    onRetry,
    onReact,
    onForward
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [lightboxUrl, setLightboxUrl] = useState(null);
    const [showReactionPicker, setShowReactionPicker] = useState(false);

    const QUICK_REACTIONS = ['❤️', '😊', '👍', '😂', '😮', '😢'];

    // Parse reactions object gracefully for both old and new DB structures
    // Enforce ONE reaction per user (clean up legacy DB inconsistencies)
    const reactions = msg.reactions || {};
    const userToEmoji = {};

    Object.entries(reactions).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
            // Old Format: { emoji: { userId: true } }
            const emoji = key;
            Object.keys(value).forEach(uid => {
                if (!userToEmoji[uid]) userToEmoji[uid] = emoji; // Keep only first encountered
            });
        } else if (typeof value === 'string') {
            // New Format: { userId: emoji }
            userToEmoji[key] = value; // Always overwrite with the new format (it's the source of truth)
        }
    });

    // Group by emoji: { emoji: { userId: true, ... } }
    const groupedReactions = {};
    Object.entries(userToEmoji).forEach(([uid, emoji]) => {
        if (!groupedReactions[emoji]) groupedReactions[emoji] = {};
        groupedReactions[emoji][uid] = true;
    });

    const reactionCounts = Object.entries(groupedReactions).reduce((acc, [emoji, users]) => {
        const count = Object.keys(users).length;
        if (count > 0) acc.push({ emoji, count, reacted: users[currentUser?.uid || currentUser?.id] });
        return acc;
    }, []);

    // Resolve sender info for group chats
    const senderUser = isGroup && !isMe
        ? allUsers.find(u => (u.uid || u.id) === msg.senderId) || null
        : null;
    const senderName = senderUser?.name || senderUser?.displayName || msg.senderName || 'Unknown';
    const senderPhoto = senderUser?.photo || senderUser?.photoURL || null;

    // Format typography based on user roles
    const isSystem = msg.type === "system";

    if (isSystem) {
        return (
            <div className="flex justify-center my-4">
                <span className="text-[11px] text-slate-500 italic bg-slate-100/80 backdrop-blur-sm px-3 py-1 rounded-full">
                    {msg.text}
                </span>
            </div>
        );
    }

    // Soft-deleted message (for everyone)
    if (msg.deleted && msg.deleteType === 'for_everyone') {
        return (
            <div id={`message-${msg.id}`} className={`flex w-full mb-3 ${isMe ? 'justify-end' : 'justify-start'}`}>
                <div className="px-3 py-2 rounded-2xl italic text-sm bg-slate-100 text-slate-400">
                    This message was deleted
                </div>
            </div>
        );
    }

    // Helper: File Type Detection & Preview
    const renderFilePreview = (fileUrl, fileName, fileSize) => {
        const ext = fileName?.split('.').pop().toLowerCase();
        const isPDF = ext === 'pdf';
        const isOffice = ['xls', 'xlsx', 'doc', 'docx', 'ppt', 'pptx'].includes(ext);

        return (
            <div className="mt-1">
                {/* PDF PREVIEW */}
                {isPDF && (
                    <div className="rounded-lg overflow-hidden border border-gray-200 mb-2 bg-slate-100 h-64 relative group/pdf">
                        <iframe
                            src={`${fileUrl}#toolbar=0`}
                            className="w-full h-full"
                            title={fileName}
                        />
                        <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute inset-0 bg-black/0 group-hover/pdf:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover/pdf:opacity-100"
                        >
                            <span className="bg-white px-3 py-1 rounded-full text-sm font-medium shadow-sm text-gray-700">Click to Open</span>
                        </a>
                    </div>
                )}

                {/* OFFICE PREVIEW */}
                {isOffice && (
                    <div className="rounded-lg overflow-hidden border border-gray-200 mb-2 bg-slate-100 h-64">
                        <iframe
                            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`}
                            className="w-full h-full border-0"
                            title={fileName}
                        />
                    </div>
                )}

                {/* STANDARD FILE CARD */}
                <div className={`flex items-center gap-3 p-2.5 rounded-xl ${isMe ? "bg-white/10" : "bg-gray-50 border border-gray-100"}`}>
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                        <FileText className="text-indigo-500" size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold truncate ${isMe ? "text-white" : "text-gray-800"}`}>
                            {fileName}
                        </p>
                        <p className={`text-[10px] ${isMe ? "text-indigo-200" : "text-gray-400"}`}>
                            {fileSize}
                        </p>
                    </div>
                    <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`p-1.5 rounded-full transition-colors ${isMe ? "hover:bg-white/20" : "hover:bg-gray-200 text-gray-500"}`}
                    >
                        <Download size={16} />
                    </a>
                </div>
            </div>
        );
    };

    return (
        <>
        <div
            id={`message-${msg.id}`}
            className={`flex w-full mb-3 ${isMe ? "justify-end" : "justify-start"} group relative`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Group chat: sender avatar */}
            {isGroup && !isMe && (
                <div className="flex-shrink-0 mr-2 mt-auto mb-1">
                    {senderPhoto ? (
                        <img src={senderPhoto} alt="" className="w-7 h-7 rounded-full object-cover border border-slate-200" />
                    ) : (
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center text-white text-[10px] font-bold border border-slate-200">
                            {senderName.substring(0, 2).toUpperCase()}
                        </div>
                    )}
                </div>
            )}
            <div className={`flex flex-col ${isMe ? "items-end" : "items-start"} max-w-[85%] md:max-w-[70%]`}>

                {/* Group chat: sender name */}
                {isGroup && !isMe && (
                    <span className="text-[10px] font-semibold text-indigo-500 font-bold mb-0.5 px-2 truncate max-w-[200px]">
                        {senderName}
                    </span>
                )}

                {/* Pinned Indicator */}
                {msg.pinned && (
                    <div className="flex items-center gap-1 text-[10px] text-indigo-500 font-bold mb-1 px-2 animate-in fade-in slide-in-from-bottom-1">
                        <Pin size={10} /> Pinned
                    </div>
                )}

                <div
                    className={`
                        relative transition-all duration-200
                        ${msg.type === 'sticker'
                            ? 'bg-transparent p-0'
                            : `px-6 py-3.5 text-sm leading-relaxed ${isMe
                                ? 'sent-bubble max-w-[80%] p-4 rounded-3xl shadow-md'
                                : 'received-bubble max-w-[80%] p-4 rounded-3xl shadow-sm text-slate-700'
                            }`
                        }
                    `}
                >
                    {/* REPLY CONTEXT */}
                    {msg.replyTo && (
                        <div
                            className={`text-xs mb-2 p-2 rounded-lg border-l-4 cursor-pointer hover:opacity-80 transition-opacity
                            ${isMe ? "bg-indigo-500/30 border-white/50 text-indigo-50" : "bg-gray-50 border-indigo-500 text-gray-600"}
                        `}
                        >
                            <span className="font-bold block mb-0.5">
                                {msg.replyTo.sender === currentUser?.uid ? "You" : activeChatName}
                            </span>
                            <span className="truncate block opacity-90">
                                {msg.replyTo.text}
                            </span>
                        </div>
                    )}

                    {/* CONTENT */}
                    <div className="text-sm leading-relaxed whitespace-pre-wrap break-words font-normal min-w-[50px]">
                        {msg.type === "text" && msg.text}

                        {msg.type === "image" && (
                            <div
                                className="rounded-lg overflow-hidden mt-1 cursor-pointer hover:brightness-95 transition-all"
                                onDoubleClick={() => setLightboxUrl(msg.fileUrl)}
                                onClick={() => setLightboxUrl(msg.fileUrl)}
                            >
                                <img
                                    src={msg.fileUrl}
                                    alt="attachment"
                                    loading="lazy"
                                    className="max-w-full h-auto object-cover max-h-[300px]"
                                />
                            </div>
                        )}

                        {msg.type === "sticker" && (
                            <div className="mt-1">
                                <img
                                    src={msg.fileUrl}
                                    alt="sticker"
                                    loading="lazy"
                                    className="w-32 h-32 object-contain"
                                    style={{ background: 'transparent' }}
                                />
                            </div>
                        )}

                        {msg.type === "file" && renderFilePreview(msg.fileUrl, msg.fileName, msg.fileSize)}
                    </div>

                    {/* META INFO (Time & status) */}
                    <div className={`flex items-center justify-end gap-1 mt-1 whitespace-nowrap ${isMe ? "text-indigo-200" : "text-gray-400"}`}>
                        <span className="text-[9px] font-medium whitespace-nowrap">
                            {new Date(msg.timestamp).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit"
                            })}
                        </span>
                        {msg.edited && (
                            <span className={`ml-1 text-[10px] italic whitespace-nowrap ${isMe ? 'text-indigo-200' : 'text-gray-400'}`}>(edited)</span>
                        )}
                        {isMe && msg.status !== 'failed' && (
                            <span className="animate-in fade-in duration-300">
                                {msg.seen || msg.status === 'seen' ? (
                                    <CheckCheck size={12} className="text-blue-400" />
                                ) : msg.delivered || msg.status === 'delivered' ? (
                                    <CheckCheck size={12} />
                                ) : (
                                    <Check size={12} />
                                )}
                            </span>
                        )}
                        {msg.status === 'failed' && (
                            <span className="flex items-center gap-1">
                                <span className="text-red-400 text-[10px]">Failed</span>
                                {onRetry && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onRetry(msg); }}
                                        className="text-[10px] text-red-500 hover:text-red-700 underline font-medium"
                                    >
                                        Retry
                                    </button>
                                )}
                            </span>
                        )}
                    </div>

                    {/* ACTIONS (SIDE - OUTSIDE BUBBLE) */}
                    <div
                        className={`
                            absolute top-1/2 -translate-y-1/2 z-10
                            ${isMe ? "right-full mr-2" : "left-full ml-2"}
                            transition-all duration-200
                            ${isHovered ? "opacity-100 translate-x-0" : "opacity-0 translate-x-2 pointer-events-none"}
                        `}
                    >
                        <div className={`rounded-full shadow-sm bg-white border border-gray-100`}>
                            <MessageActions
                                message={msg}
                                isMe={isMe}
                                canPin={canPin}
                                onReply={onReply}
                                onForward={onForward}
                                onPin={onPin}
                                onUnpin={onUnpin}
                                onTask={onTask}
                                onDelete={onDelete}
                                onReminder={onReminder}
                                miniMode={true}
                            />
                        </div>
                    </div>
                </div>

                {/* EMOJI REACTIONS DISPLAY */}
                {reactionCounts.length > 0 && (
                    <div className={`flex gap-1.5 mt-2 ${isMe ? 'justify-end pr-1' : 'justify-start pl-1'}`}>
                        {reactionCounts.map(({ emoji, count, reacted }) => (
                            <button
                                key={emoji}
                                onClick={() => onReact?.(msg, emoji)}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border transition-all hover:scale-105 cursor-pointer ${
                                    reacted
                                        ? 'bg-indigo-50 border-indigo-300 text-indigo-700 shadow-sm'
                                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                                }`}
                            >
                                <span className="text-sm">{emoji}</span>
                                <span className="font-semibold text-[11px]">{count}</span>
                            </button>
                        ))}
                    </div>
                )}

                {/* QUICK REACTION PICKER — Never on own messages */}
                {isHovered && msg.type !== 'sticker' && !isMe && (
                    <div className={`absolute ${isMe ? 'right-0' : 'left-0'} -top-10 z-30 transition-all duration-200`}>
                        <div className="flex gap-1 glass-morphism shadow-xl rounded-full px-2 py-1.5 border border-slate-200/50">
                            {QUICK_REACTIONS.map(emoji => (
                                <button
                                    key={emoji}
                                    onClick={(e) => { e.stopPropagation(); onReact?.(msg, emoji); }}
                                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-indigo-50 hover:scale-125 transition-all text-base cursor-pointer"
                                >
                                    {emoji}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>

            {/* Image Lightbox via Portal */}
            {lightboxUrl && ReactDOM.createPortal(
                <div
                    className="fixed inset-0 bg-black/90 z-[99999] flex items-center justify-center p-4 cursor-zoom-out"
                    onClick={() => setLightboxUrl(null)}
                >
                    <button
                        onClick={() => setLightboxUrl(null)}
                        className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                    >
                        ✕
                    </button>
                    <img
                        src={lightboxUrl}
                        alt="Full view"
                        className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                        onDoubleClick={() => setLightboxUrl(null)}
                    />
                    <a
                        href={lightboxUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="absolute bottom-6 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2"
                    >
                        <Download size={14} /> Open Original
                    </a>
                </div>,
                document.body
            )}
        </>
    );
}, (prev, next) => {
    return (
        prev.msg.id === next.msg.id &&
        prev.msg.pinned === next.msg.pinned &&
        prev.msg.status === next.msg.status &&
        prev.msg.delivered === next.msg.delivered &&
        prev.msg.seen === next.msg.seen &&
        prev.msg.edited === next.msg.edited &&
        prev.msg.deleted === next.msg.deleted &&
        prev.msg.reactions === next.msg.reactions &&
        prev.isMe === next.isMe
    );
});

export default MessageBubble;
